import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { Trip, TripDirection, TransportRoute } from 'contracts/transport-routes';
import { useTranslation } from 'hooks';
import { Card, CardContent } from 'components/ui/card';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs } from 'components/ui/Forms';
import { cn } from 'lib/utils';

import {
  TripStopsManager,
  type TripStopsManagerRef,
} from '../trip-stops-manager';
import { TollsPreview } from '../tolls-preview';
import { useTripManagerState } from './hooks';
import { translations } from './translations';
import { formSchema, FormSchema } from './form-schema';

type TripData = TransportRoute['forward_trip' | 'backward_trip'];

type Ref = {
  getFormData: () => Promise<Trip>;
  setTripData: (trip: TripData) => void;
}

type Props = React.HTMLAttributes<HTMLDivElement> & {
  title?: React.ReactNode;
  tripDirection: TripDirection;
}

export type TripManagerRef = Ref;

export const TripManager = React.forwardRef<Ref, Props>(({
                                                           title,
                                                           tripDirection,
                                                           className,
                                                           ...props
                                                         }, ref) => {
  const { getTranslation: t } = useTranslation(translations);

  const tripStopsManagerRef = React.useRef<TripStopsManagerRef>(null);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const {
    originCityOptions,
    destinationCityOptions,
    ufOptions,
    onOriginUfChange,
    onOriginIbgeChange,
    onDestinationUfChange,
    onDestinationIbgeChange,
  } = useTripManagerState({ tripDirection: tripDirection, form });

  const onReceiveTripData = React.useCallback((trip: TripData) => {
    const { origin, destination } = trip;

    form.setValue('origin_uf', origin.uf);
    onOriginUfChange(origin.uf);
    form.setValue('destination_uf', destination.uf);
    onDestinationUfChange(destination.uf);

    form.setValue('origin_ibge', origin.ibge);
    onOriginIbgeChange(origin.ibge);
    form.setValue('destination_ibge', destination.ibge);
    onDestinationIbgeChange(destination.ibge);

    tripStopsManagerRef.current?.setStopLocations(trip.stops);
  }, [
    form,
    onOriginUfChange,
    onDestinationUfChange,
    onOriginIbgeChange,
    onDestinationIbgeChange,
  ]);

  const getFormValues = React.useCallback(async (): Promise<FormSchema> => {
    const isFormValid = await form.trigger();

    if (!isFormValid) {
      throw new Error('Trip manager form has invalid data');
    }

    return form.getValues();
  }, [form]);

  const getFormData = React.useCallback(async (): Promise<Trip> => {
    const formValues = await getFormValues();

    return {
      origin: {
        ibge: formValues.origin_ibge,
        uf: formValues.origin_uf,
      },
      destination: {
        ibge: formValues.destination_ibge,
        uf: formValues.destination_uf,
      },
      stops: tripStopsManagerRef.current?.getStopLocations() || [],
    };
  }, [getFormValues]);

  React.useImperativeHandle(ref, () => ({
    getFormData,
    setTripData: onReceiveTripData,
  }), [getFormData, onReceiveTripData]);

  return (
    <div {...props} className={cn('w-full space-y-6', className)}>
      {title && <div className={'text-gray-600 font-GilroyBold text-[18px]'}>
        {title}
      </div>}
      <Card className={'bg-gray-200'}>
        <CardContent className={'pt-6'}>
          <Card>
            <CardContent className={'p-6 bg-white'}>
              <Form {...form}>
                <form>
                  <div className={'grid grid-cols-4 gap-4'}>
                    <FormField
                      control={form.control}
                      name={'origin_uf'}
                      render={({ field }) => (
                        <ComboboxGs
                          label={t('originUf')}
                          form={form}
                          fieldName={field.name}
                          fieldValue={field.value}
                          content={ufOptions.singleSelectOptions}
                          onChange={onOriginUfChange}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={'origin_ibge'}
                      render={({ field }) => (
                        <ComboboxGs
                          label={t('originCity')}
                          form={form}
                          fieldName={field.name}
                          fieldValue={field.value}
                          content={originCityOptions}
                          onChange={onOriginIbgeChange}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={'destination_uf'}
                      render={({ field }) => (
                        <ComboboxGs
                          label={t('destinationUf')}
                          form={form}
                          fieldName={field.name}
                          fieldValue={field.value}
                          content={ufOptions.singleSelectOptions}
                          onChange={onDestinationUfChange}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={'destination_ibge'}
                      render={({ field }) => (
                        <ComboboxGs
                          label={t('destinationCity')}
                          form={form}
                          fieldName={field.name}
                          fieldValue={field.value}
                          content={destinationCityOptions}
                          onChange={onDestinationIbgeChange}
                        />
                      )}
                    />
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
          <TripStopsManager
            ref={tripStopsManagerRef}
            tripDirection={tripDirection}
          />
          <TollsPreview
            tripDirection={tripDirection}
            className={'mt-6'}
          />
        </CardContent>
      </Card>
    </div>
  );
});
TripManager.displayName = 'TripManager';